<script>
import { mapGetters } from 'vuex'

import { apiSecond } from '@/boot/axios'

import AqField from '@/components/aqua/AqField.vue'
import AqKpi from '@/components/aqua/AqKpi.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AqStatus from '@/components/aqua/AqStatus.vue'
import SelectFish from '@/components/catalog/SelectFish.vue'
import SelectRegionList from '@/components/catalog/SelectRegionList.vue'
import SelectDistrict from '@/components/catalog/SelectDistrict.vue'
import SelectReservoirsList from '@/components/catalog/SelectReservoirsList.vue'
import SelectPlots from '@/components/catalog/SelectPlots.vue'

import GrownFishModal from './GrownFishModal.vue'

export default {
	name: 'GrownCreatePage',
	components: {
		AqField,
		AqKpi,
		AqPageHeader,
		AqStatus,
		SelectFish,
		GrownFishModal,
		SelectRegionList,
		SelectDistrict,
		SelectReservoirsList,
		SelectPlots,
	},
	props: {
		statusPage: {
			type: String,
			default: 'create',
		},
	},
	data() {
		return {
			application: null,
			catch_at: null,
			region: null,
			district: null,
			pond: null,
			plot: null,
			design_capacities: null,
			fishes: [],
			selectedFish: null,
			issues: [],
			errorMessage: {
				status: null,
				messages: null,
				text: null,
			},
			message: {
				status: null,
				text: null,
			},
			formData: null,
			dataEcp: null,
			signing: false,
		}
	},
	computed: {
		...mapGetters(['_getUserObject']),
		isNotEditable() {
			return this.statusPage == 'show'
		},
		isEditableBySubject() {
			return !!this.application && [null, undefined, 'new', 'rejected'].includes(this.application.status)
		},
		statusKey() {
			return this.application?.status || 'new'
		},
		listPath() {
			return '/' + this.$i18n.locale + '/account/logs/grown'
		},
		headerTitle() {
			if (this.isNotEditable && this.catch_at) {
				return this.$t('aqua.ui.grown.record_from', { date: this.$options.filters.formatOnlyDate(this.application?.catch_at || this.catch_at) })
			}
			return this.$t(this.$route.params.id ? 'aqua.ui.grown.edit_title' : 'aqua.ui.grown.new_title')
		},
		fishTitles() {
			return Object.fromEntries((this.$store.getters._getFishes || []).map((fish) => [fish.id, fish.title]))
		},
		capacityList() {
			return (this.design_capacities || []).map((item) => ({
				fish_id: item.fish_id,
				title: this.fishTitles[item.fish_id] || `#${item.fish_id}`,
				volume: Number(item.volume) || 0,
			}))
		},
		totalGrown() {
			return this.fishes.reduce((sum, fish) => sum + (Number(fish.actual_production_by_species) || 0), 0)
		},
	},
	watch: {
		plot() {
			this.design_capacities = null
			this.getDesignCapacity()
		},
		catch_at() {
			this.design_capacities = null
			this.getDesignCapacity()
		},
	},
	methods: {
		async getDesignCapacity() {
			try {
				if (!this.plot || !this.catch_at) return
				const response = await apiSecond.get('v1/grown/design-capacity/', {
					params: {
						catch_at: this.catch_at,
						plot_id: this.plot.id,
					},
				})

				if (response?.data?.data) {
					this.design_capacities = response?.data?.data
				}
			} catch (error) {
				this.catchError(error)
			}
		},
		catchError(error) {
			if (error?.response?.status == 500) {
				this.errorMessage.status = 500
				this.errorMessage.text = this.$t('system_message.500')
			}
			if (error?.response?.status == 401) {
				this.errorMessage.status = 401
				this.errorMessage.text = error.response.data.error_message
				this.$router.push('/' + this.$i18n.locale + '/login')
			}
			if (error?.response?.status == 422) {
				const fieldErrors = Object.values(error.response.data.error_messages || {}).flat()
				this.errorMessage.status = 422
				this.errorMessage.text = error.response.data.error_message || null
				if (fieldErrors.length) this.showIssues(fieldErrors)
			}
			if (error?.response?.status == 403 || error?.response?.status == 404) {
				this.errorMessage.status = 403
				this.errorMessage.text = error?.response?.data?.message
			}
		},
		async apiGet() {
			if (!this.$route.params.id) return
			try {
				const response = await apiSecond.get('v1/grown/' + this.$route.params.id)
				if (response.data.data) {
					this.application = response.data.data
					if (this.application.catch_at) {
						this.catch_at = String(this.application.catch_at).slice(0, 10)
					}
					if (this.application.plot) {
						this.plot = this.application.plot
						if (this.application.plot.reservoir) this.pond = this.application.plot.reservoir
						if (this.application.plot.region) this.region = this.application.plot.region
						if (this.application.plot.district) this.district = this.application.plot.district
					}
					if (this.application.fishs && this.application.fishs.length) {
						this.fishes = this.application.fishs.map((e, ind) => {
							return {
								id: new Date().getTime() + '-' + ind,
								type: {
									id: e.fish_id,
									title: e.fishName,
								},
								_id: e.id,
								water_supply_source_id: {
									id: e.water_supply_source?.id,
									title: e.water_supply_source?.title,
								},
								water_supply_source_volume: e.water_supply_source_volume,
								design_capacity: e.design_capacity,
								actual_production_by_species: e.actual_production_by_species,
								sold_volume: e.sold_volume,
								juvenile_production_by_age: e.juvenile_production_by_age,
								juvenile_sold: e.juvenile_sold,
								broodstock_production: e.broodstock_production,
								broodstock_sold: e.broodstock_sold,
								feed_consumption_tons: e.feed_consumption_tons,
								feed_info: e.feed_info,
							}
						})
					}
				}
			} catch (error) {
				this.catchError(error)
			}
		},
		async startEcpSigning() {
			try {
				this.signing = true
				this.dataEcp = null
				this.errorMessage.status = null
				this.errorMessage.text = null

				// Check if NCALayer is available
				if (!this.isNCALayerAvailable()) {
					throw new Error('NCALayer not available')
				}

				const { NCALayerClientIns } = await import('@/shared/services/nca')
				const request = {
					module: 'kz.gov.pki.knca.commonUtils',
					method: 'signXml',
					args: [
						'PKCS12',
						'SIGNATURE',
						'<login><sessionid>caacda70-fd36-45ed-8d94-45a88890f83a</sessionid></login>',
						'',
						'',
					],
				}

				const xml = await NCALayerClientIns.request(request)
				this.dataEcp = xml
				if (xml) {
					await this.sendEcpSignature(xml)
				}
			} catch (error) {
				this.handleEcpError(error)
			} finally {
				this.signing = false
			}
		},
		isNCALayerAvailable() {
			// Check if we're in a secure context (HTTPS or localhost)
			if (!window.isSecureContext && location.hostname !== 'localhost') {
				return false
			}
			// Check if WebSocket is supported
			if (!window.WebSocket) {
				return false
			}
			return true
		},
		handleEcpError(error) {
			let message = this.$t('system_message.error_occurred')
			let status = 500

			// Handle specific error types
			if (error?.message) {
				const errorMessage = error.message.toLowerCase()
				if (errorMessage.includes('ncalayer not available') || errorMessage.includes('websocket')) {
					message = this.$t('system_message.nca_not_available')
					status = 503
				} else if (errorMessage.includes('timeout')) {
					message = this.$t('system_message.nca_timeout')
					status = 408
				} else if (errorMessage.includes('certificate') || errorMessage.includes('cert')) {
					message = this.$t('system_message.nca_certificate_error')
					status = 400
				} else if (errorMessage.includes('connection')) {
					message = this.$t('system_message.nca_connection_failed')
					status = 503
				} else if (errorMessage.includes('ncalayer')) {
					message = this.$t('system_message.nca_not_running')
					status = 503
				} else if (errorMessage.includes('user cancelled') || errorMessage.includes('cancelled')) {
					message = this.$t('system_message.nca_user_cancelled')
					status = 400
				} else if (errorMessage.includes('invalid response')) {
					message = this.$t('system_message.nca_invalid_response')
					status = 500
				}
			}

			// Handle HTTP errors from API
			if (error?.response) {
				status = error.response.status
				if (error.response.data?.message) {
					message = error.response.data.message
				} else if (error.response.status === 401) {
					message = this.$t('system_message.unauthorized')
				} else if (error.response.status === 403) {
					message = this.$t('system_message.forbidden')
				} else if (error.response.status === 404) {
					message = this.$t('system_message.not_found')
				} else if (error.response.status === 422) {
					message = this.$t('system_message.validation_error')
				} else if (error.response.status >= 500) {
					message = this.$t('system_message.server_error')
				}
			}

			this.errorMessage.status = status
			this.errorMessage.text = message
			this.$toast.error(message)
		},
		async sendEcpSignature(xml) {
			try {
				const formData = new FormData()
				formData.append('xml', xml)
				formData.append('document_id', this.$route.params.id)
				const response = await apiSecond.post(`/v1/grown/sign`, formData)

				this.$toast.success(
					response.data.message || this.$t('reporting.notifications.signed_success'),
				)

				this.message.status = 200
				this.message.text =
					response.data.message || this.$t('reporting.notifications.signed_success')
				// Refresh application data to show updated signed status
				await this.apiGet()
			} catch (error) {
				// Show error toast
				const errorMessage =
					error?.response?.data?.message || this.$t('system_message.error_occurred')
				this.$toast.error(errorMessage)

				this.errorMessage.status = error?.response?.status || 500
				this.errorMessage.text = errorMessage
			}
		},
		async sendEcpKey(xml) {
			try {
				const formData = new FormData()
				formData.append('xml', xml)
				await apiSecond.post(`/v1/grown/sign/${this.$route.params.id}`, formData)
				this.message.status = 200
				this.message.text = this.$t('reporting.notifications.signed_success')
				await this.apiGet()
			} catch (error) {
				this.errorMessage.status = error?.response?.status || 500
				this.errorMessage.text =
					error?.response?.data?.message || this.$t('system_message.error_occurred')
			}
		},
		validateFirm() {
			this.errorMessage.status = null
			this.errorMessage.messages = null
			this.errorMessage.text = null

			const messages = { fish: [] }
			const issues = []
			if (!this.catch_at) issues.push(this.$t('aqua.ui.grown.issue_date'))
			if (!this.plot) issues.push(this.$t('aqua.ui.grown.issue_plot'))
			if (!this.fishes.length) issues.push(this.$t('aqua.ui.grown.issue_no_fish'))
			this.fishes.forEach((element, index) => {
				if (element.type == null) {
					messages.fish[element.id] = this.$t('system_message.required_message')
					issues.push(this.$t('aqua.ui.grown.issue_fish_type', { n: index + 1 }))
				}
			})

			if (issues.length) {
				this.errorMessage.messages = messages
				this.showIssues(issues)
			} else {
				this.issues = []
				this.onSubmit()
			}
		},
		showIssues(issues) {
			this.issues = issues
			this.$nextTick(() => this.$refs.issuesAlert?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
		},
		onSubmit() {
			this.message.status = null
			this.message.text = null

			const formData = new FormData()

			if (this.$route.params.id) formData.append('grown_id', this.$route.params.id)
			formData.append('plot_id', this.plot.id)
			formData.append('user_id', this.$store.state.userObject.id)
			formData.append('catch_at', this.catch_at)
			this.fishes.forEach((fish, ind) => {
				const values = {
					id: fish._id,
					fish_id: fish.type?.id,
					water_supply_source_id: fish.water_supply_source_id?.id,
					water_supply_source_volume: fish.water_supply_source_volume,
					design_capacity: fish.design_capacity,
					actual_production_by_species: fish.actual_production_by_species,
					sold_volume: fish.sold_volume,
					juvenile_production_by_age: fish.juvenile_production_by_age,
					juvenile_sold: fish.juvenile_sold,
					broodstock_production: fish.broodstock_production,
					broodstock_sold: fish.broodstock_sold,
					feed_consumption_tons: fish.feed_consumption_tons,
					feed_info: fish.feed_info,
				}
				Object.entries(values).forEach(([key, value]) => {
					if (key === 'id' && !value) return
					formData.append(`fish[${ind}][${key}]`, value === null || value === undefined ? '' : String(value))
				})
			})

			apiSecond
				.post(this.$route.params.id ? '/v1/grown/update' : '/v1/grown/create', formData)
				.then((response) => {
					if (response.status == 200) {
						this.message.status = 200
						this.message.text = response.data.message
						this.$toast.success(
							response.data.message ||
								this.$t(this.$route.params.id ? 'toast.saved' : 'toast.created'),
						)
						this.$router.push({
							path: '/' + this.$i18n.locale + '/account/logs/grown/' + response.data.data.id,
						})
					}
				})
				.catch(this.catchError.bind(this))
		},
		async confirmDelete() {
			const confirmed = await this.$dialog.confirm({
				title: this.$t('system_message.confirm_delete'),
				confirmText: this.$t('button.delete'),
			})

			if (confirmed) {
				try {
					await apiSecond.delete(`/v1/grown/destroy/${this.$route.params.id}`)
					this.message.status = 200
					this.message.text = this.$t('system_message.deleted_successfully')
					this.$toast.success(this.$t('toast.deleted'))
					setTimeout(() => {
						this.$router.push({
							path: '/' + this.$i18n.locale + '/account/logs/grown',
						})
					}, 1500)
				} catch (error) {
					this.errorMessage.status = error?.response?.status || 500
					this.errorMessage.text =
						error?.response?.data?.message || this.$t('system_message.error_occurred')
					this.$toast.error(this.errorMessage.text)
				}
			}
		},
		openModal(fish) {
			this.selectedFish = fish
			this.$modal.show('GrownFishModal')
		},
		closeModal() {
			this.selectedFish = null
			this.$modal.hide('GrownFishModal')
		},
		acceptModal(fish) {
			if (!this.selectedFish) {
				this.fishes.push({
					id: new Date().getTime(),
					...fish,
				})
			} else {
				const findedInd = this.fishes.findIndex((f) => f.id === this.selectedFish.id)
				if (findedInd > -1 && this.fishes[findedInd]) {
					this.fishes[findedInd] = {
						...this.fishes[findedInd],
						...fish,
					}
				}
			}

			this.closeModal()
		},
		removeFish(index) {
			this.fishes.splice(index, 1)
		},
		async prefillSinglePlot() {
			const response = await apiSecond
				.get('/v2/handbook-plots', { params: { page: 1, per_page: 2 } })
				.catch(() => null)
			const plots = response?.data?.data || []
			if (plots.length !== 1 || this.plot) return
			const plot = plots[0]
			if (plot.region) this.region = plot.region
			if (plot.district) this.district = plot.district
			if (plot.reservoir) this.pond = plot.reservoir
			this.plot = plot
		},
		formatNum(value) {
			if (value === null || value === undefined || value === '') return '—'
			const number = Number(value)
			return Number.isNaN(number) ? value : number.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
		},
		fishPercent(fish) {
			const design = Number(fish.design_capacity)
			if (!design) return null
			return Math.round(((Number(fish.actual_production_by_species) || 0) / design) * 100)
		},
	},
	beforeMount() {
		this.apiGet()
		if (this.statusPage === 'create') {
			this.catch_at = new Date().toLocaleDateString('sv-SE')
			this.prefillSinglePlot()
		}
		if (!(this.$store.getters._getFishes || []).length) this.$store.dispatch('_fetchFishes')
	},
	beforeCreate() {
		if (!localStorage.token) {
			this.$router.push('/ru/login')
		}
	},
}
</script>

<template>
	<div
		class="wrap__content aq-page"
		v-bind:class="{
			'wrap__content--preloader': !$store.state.userObject,
		}"
	>
		<v-preloader v-if="!$store.state.userObject" :message="errorMessage"></v-preloader>

		<div v-if="$store.state.userObject" class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['GrownManagement', 'TradeManagementMain']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="headerTitle"
								:eyebrow="$t('titles.grownJournals')"
								:back="listPath"
								:back-label="$t('aqua.ui.grown.to_list')"
							>
								<template v-if="application" #meta>
									<AqStatus :value="statusKey" :label="$t('statusses.readGrownAll.' + statusKey)" />
									<span v-if="plot" class="aq-meta">{{ plot.title }}</span>
								</template>
							</AqPageHeader>

							<div class="aq-layout">
								<form id="aq-grown-form" class="aq-layout__main" method="POST" novalidate @submit.prevent="validateFirm">
									<div v-if="issues.length" ref="issuesAlert" class="aq-alert aq-alert--danger" role="alert">
										<svg class="aq-alert__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<circle cx="12" cy="12" r="9" />
											<path d="M12 7.5v5.5M12 16.5v.5" />
										</svg>
										<div>
											<strong>{{ $t('aqua.ui.grown.issues_title') }}</strong>
											<ul class="aq-issues">
												<li v-for="(issue, index) in issues" :key="index">{{ issue }}</li>
											</ul>
										</div>
									</div>

									<div v-if="application && isEditableBySubject && application.reject_reason" class="aq-alert aq-alert--danger">
										<svg class="aq-alert__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 5h16v11H8l-4 4z" />
										</svg>
										<div>
											<strong>{{ $t('grown_journal.reject_reason') }}</strong>
											<p class="aq-alert__text">{{ application.reject_reason }}</p>
											<p class="aq-alert__text aq-alert__text--muted">{{ $t('aqua.ui.grown.reject_hint') }}</p>
										</div>
									</div>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">1</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.grown.section_place') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.grown.section_place_hint') }}</p>
											</div>
										</div>
										<div class="aq-card__body aq-grid">
											<AqField
												:label="$t('labels.reportPeriod')"
												:required="!isNotEditable"
												:invalid="!!(errorMessage.messages && errorMessage.messages.catch_at)"
												:error="errorMessage.messages && errorMessage.messages.catch_at ? errorMessage.messages.catch_at.join(' ') : ''"
											>
												<div v-if="isNotEditable" class="aq-value">{{ application && application.catch_at ? $options.filters.formatOnlyDate(application.catch_at) : '—' }}</div>
												<input
													v-else
													v-model="catch_at"
													type="date"
													class="aq-input"
													:max="new Date().toISOString().split('T')[0]"
													name="catch_at"
												/>
											</AqField>
											<AqField :label="$t('inputs.region')" :required="!isNotEditable">
												<div v-if="isNotEditable" class="aq-value">{{ region ? region.title : '—' }}</div>
												<SelectRegionList
													v-else
													class="aq-legacy"
													:value="region"
													:required="true"
													:disabled="isNotEditable"
													:with-title="false"
													:multiple="false"
													:placeholder="$t('labels.select_region')"
													:confinesValue="_getUserObject?.quota_regions"
													@input="region = $event"
												/>
											</AqField>
											<AqField :label="$t('handbooks.Район')">
												<div v-if="isNotEditable" class="aq-value">{{ district ? district.title : '—' }}</div>
												<SelectDistrict
													v-else
													class="aq-legacy"
													:value="district"
													:disabled="isNotEditable || !region"
													:filters="{
														regions: region?.id ? [region?.id] : null,
													}"
													:watch-values="[region]"
													:with-title="false"
													:multiple="false"
													:placeholder="$t('filters.select_district')"
													:confinesValue="_getUserObject?.quota_districts"
													@input="district = $event"
												/>
											</AqField>
											<AqField :label="$t('inputs.pond_name')" :required="!isNotEditable">
												<div v-if="isNotEditable" class="aq-value">{{ pond ? pond.title : '—' }}</div>
												<SelectReservoirsList
													v-else
													class="aq-legacy"
													:value="pond"
													:required="true"
													:disabled="isNotEditable || !district"
													:filters="{ districts: district?.id ? [district?.id] : null }"
													:watch-values="[district]"
													:with-title="false"
													:multiple="false"
													:placeholder="$t('placeholder.select_reservoir')"
													:confinesValue="_getUserObject?.quota_ponds"
													@input="pond = $event"
												/>
											</AqField>
											<AqField :label="$t('handbooks.Участки')" :required="!isNotEditable" wide>
												<div v-if="isNotEditable" class="aq-value">{{ plot ? plot.title : '—' }}</div>
												<SelectPlots
													v-else
													class="aq-legacy"
													:value="plot"
													:required="true"
													:disabled="isNotEditable || !pond"
													:filters="{
														regions: region?.id ? [region?.id] : null,
														districts: district?.id ? [district?.id] : null,
														ponds: pond?.id ? [pond?.id] : null,
														date: catch_at,
													}"
													:watch-values="[region, district, pond, catch_at]"
													:with-title="false"
													:multiple="false"
													:placeholder="$t('placeholder.select_plot')"
													:confinesValue="_getUserObject?.quota_plots"
													@input="plot = $event"
												/>
											</AqField>
										</div>
									</section>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">2</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.grown.section_capacity') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.grown.section_capacity_hint') }}</p>
											</div>
										</div>
										<div class="aq-card__body">
											<div v-if="capacityList.length" class="aq-capacity">
												<AqKpi
													v-for="item in capacityList"
													:key="item.fish_id"
													:label="item.title"
													:value="formatNum(item.volume)"
													:unit="$t('aqua.ui.grown.unit_t')"
												/>
											</div>
											<p v-else class="aq-muted-note">
												<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
													<circle cx="12" cy="12" r="9" />
													<path d="M12 8v5M12 16v.5" />
												</svg>
												{{ plot && catch_at ? $t('aqua.ui.grown.capacity_none') : $t('aqua.ui.grown.capacity_pick') }}
											</p>
										</div>
									</section>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">3</span>
											<div>
												<h2 class="aq-card__title">{{ $t('labels.objectAqua') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.grown.fishes_count', { count: fishes.length }) }}</p>
											</div>
										</div>
										<div class="aq-card__body">
											<div v-if="fishes.length" class="aq-fishes">
												<article v-for="(fish, fishInd) in fishes" :key="fish.id" class="aq-fish">
													<header class="aq-fish__head">
														<span class="aq-fish__num">{{ fishInd + 1 }}</span>
														<strong class="aq-fish__title">{{ fish.type && fish.type.title ? fish.type.title : '—' }}</strong>
														<div class="aq-fish__actions">
															<template v-if="!isNotEditable">
																<button
																	type="button"
																	class="aq-btn aq-btn--ghost aq-btn--icon"
																	:aria-label="$t('tooltip.edit')"
																	:title="$t('tooltip.edit')"
																	@click.stop.prevent="openModal(fish)"
																>
																	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
																		<path d="M4 20h4L19 9l-4-4L4 16z" />
																	</svg>
																</button>
																<button
																	type="button"
																	class="aq-btn aq-btn--danger aq-btn--icon"
																	:aria-label="$t('tooltip.remove')"
																	:title="$t('tooltip.remove')"
																	@click.stop.prevent="removeFish(fishInd)"
																>
																	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
																		<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
																	</svg>
																</button>
															</template>
														</div>
													</header>
													<div class="aq-fish__progress">
														<div class="aq-fish__progress-label">
															<span>{{ $t('aqua.ui.grown.grown_of_capacity') }}</span>
															<strong>
																{{ formatNum(fish.actual_production_by_species) }}
																<template v-if="fish.design_capacity">/ {{ formatNum(fish.design_capacity) }}</template>
																{{ $t('aqua.ui.grown.unit_t') }}
															</strong>
														</div>
														<div
															v-if="fishPercent(fish) !== null"
															class="aq-bar"
															:class="{ 'aq-bar--over': fishPercent(fish) > 100 }"
															role="progressbar"
															:aria-valuenow="fishPercent(fish)"
															aria-valuemin="0"
															aria-valuemax="100"
														>
															<span :style="{ width: Math.min(fishPercent(fish), 100) + '%' }"></span>
														</div>
														<span v-if="fishPercent(fish) > 100" class="aq-fish__warn">{{ $t('aqua.ui.grown.over_capacity') }}</span>
													</div>
													<dl class="aq-fish__stats">
														<div>
															<dt>{{ $t('aqua.ui.grown.sold') }}</dt>
															<dd>{{ formatNum(fish.sold_volume) }}</dd>
														</div>
														<div>
															<dt>{{ $t('aqua.ui.grown.juvenile') }}</dt>
															<dd>{{ formatNum(fish.juvenile_production_by_age) }}</dd>
														</div>
														<div>
															<dt>{{ $t('aqua.ui.grown.feed') }}</dt>
															<dd>{{ formatNum(fish.feed_consumption_tons) }}</dd>
														</div>
													</dl>
													<button v-if="isNotEditable" type="button" class="aq-fish__more" @click.stop.prevent="openModal(fish)">
														{{ $t('tooltip.look') }}
														<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
															<path d="M9 5l7 7-7 7" />
														</svg>
													</button>
												</article>
											</div>
											<div v-else-if="isNotEditable" class="aq-muted-note">{{ $t('aqua.ui.grown.fishes_empty') }}</div>

											<button v-if="!isNotEditable" type="button" class="aq-add" @click.stop.prevent="$modal.show('GrownFishModal')">
												<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
													<path d="M12 5v14M5 12h14" />
												</svg>
												{{ $t('aqua.ui.grown.add_fish') }}
											</button>
										</div>
									</section>
								</form>

								<aside class="aq-layout__side">
									<div class="aq-card aq-panel">
										<div v-if="application" class="aq-panel__status">
											<span class="aq-panel__label">{{ $t('headers.status') }}</span>
											<AqStatus :value="statusKey" :label="$t('statusses.readGrownAll.' + statusKey)" />
											<ul class="aq-timeline">
												<li v-if="application.signed_at">
													<span>{{ $t('aqua.ui.grown.signed_at') }}</span>
													<strong>{{ application.signed_at | formatDate }}</strong>
												</li>
												<li v-if="application.tir_signed_at">
													<span>{{ $t('grown_journal.approved') }}</span>
													<strong>{{ application.tir_signed_at | formatDate }}</strong>
												</li>
												<li v-if="application.rejected_at && statusKey === 'rejected'">
													<span>{{ $t('grown_journal.rejected') }}</span>
													<strong>{{ application.rejected_at | formatDate }}</strong>
												</li>
											</ul>
										</div>

										<AqKpi
											v-if="application && application.warehouse"
											tone="success"
											:label="$t('aqua.ui.grown.available_volume')"
											:value="formatNum(application.warehouse.available_volume)"
											:unit="$t('aqua.ui.grown.unit_t')"
											:hint="`${$t('aqua.ui.grown.nikad')}: ${application.warehouse.nikad}`"
										/>

										<AqKpi
											v-if="fishes.length"
											:label="$t('aqua.ui.grown.total_grown')"
											:value="formatNum(totalGrown)"
											:unit="$t('aqua.ui.grown.unit_t')"
											:hint="$t('aqua.ui.grown.fishes_count', { count: fishes.length })"
										/>

										<div v-if="message.status === 200 && message.text" class="aq-alert aq-alert--success aq-alert--compact" role="status">
											{{ message.text }}
										</div>
										<div v-if="errorMessage.text" class="aq-alert aq-alert--danger aq-alert--compact" role="alert">
											{{ errorMessage.text }}
										</div>

										<div class="aq-panel__actions">
											<template v-if="!isNotEditable">
												<button type="submit" form="aq-grown-form" class="aq-btn aq-btn--primary aq-btn--block">
													{{ $t('button.save') }}
												</button>
												<router-link
													:to="$route.params.id ? listPath + '/' + $route.params.id : listPath"
													class="aq-btn aq-btn--block"
												>
													{{ $t('button.cancel') }}
												</router-link>
											</template>
											<template v-else-if="$route.params.id">
												<template v-if="isEditableBySubject">
													<button
														type="button"
														class="aq-btn aq-btn--primary aq-btn--block"
														:disabled="signing"
														@click.prevent="startEcpSigning"
													>
														<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
															<path d="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" />
														</svg>
														<span v-if="!signing">{{ $t('grown_journal.send') }}</span>
														<span v-else>{{ $t('button.signing') }}…</span>
													</button>
													<router-link :to="listPath + '/' + $route.params.id + '/edit'" class="aq-btn aq-btn--block">
														{{ $t('tooltip.edit') }}
													</router-link>
													<button type="button" class="aq-btn aq-btn--danger aq-btn--block" @click.prevent="confirmDelete">
														{{ $t('tooltip.remove') }}
													</button>
												</template>
												<p v-else-if="application && statusKey === 'subject_signed'" class="aq-panel__hint">
													{{ $t('aqua.ui.grown.waiting_hint') }}
												</p>
											</template>
										</div>
									</div>
								</aside>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<GrownFishModal
			:fish="selectedFish"
			:isNotEditable="isNotEditable"
			:design_capacities="design_capacities"
			@close="closeModal"
			@accept="acceptModal"
		/>
	</div>
</template>

<style scoped>
.aq-issues {
	margin: 6px 0 0;
	padding-left: 18px;
	line-height: 1.6;
}
.aq-shell {
	background: var(--aq-bg);
}
.aq-meta {
	color: var(--aq-muted);
	font-size: 14px;
}
.aq-layout {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	align-items: start;
	gap: 24px;
}
.aq-layout__side {
	position: sticky;
	top: 16px;
}
.aq-alert__icon {
	flex-shrink: 0;
	margin-top: 1px;
	color: var(--aq-danger);
}
.aq-alert__text {
	margin: 4px 0 0;
	line-height: 1.5;
}
.aq-alert__text--muted {
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-alert--compact {
	margin: 0;
	padding: 10px 14px;
	font-size: 14px;
	line-height: 1.45;
}
.aq-alert--danger.aq-alert--compact {
	color: var(--aq-danger);
}
.aq-alert--success.aq-alert--compact {
	color: var(--aq-success);
}
.aq-step {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 10px;
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
	font-weight: 700;
}
.aq-value {
	min-height: 44px;
	padding: 11px 0;
	border-bottom: 1px dashed var(--aq-border-strong);
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 500;
	word-break: break-word;
}
.aq-legacy ::v-deep .multiselect__tags {
	min-height: 44px;
	padding-top: 10px;
	border: 1px solid var(--aq-border-strong) !important;
	border-radius: 10px;
	transition: border-color 0.2s var(--aq-ease);
}
.aq-legacy ::v-deep .multiselect__tags:hover,
.aq-legacy ::v-deep .multiselect--active .multiselect__tags {
	border-color: var(--aq-primary) !important;
}
.aq-legacy ::v-deep .multiselect--disabled {
	opacity: 0.6;
}
.aq-legacy ::v-deep .multiselect__single,
.aq-legacy ::v-deep .multiselect__placeholder {
	font-size: 15px;
}
.aq-capacity {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 12px;
}
.aq-capacity ::v-deep .aq-kpi {
	padding: 14px 16px;
	box-shadow: none;
	background: #fbfdff;
}
.aq-capacity ::v-deep .aq-kpi__value {
	font-size: 22px;
}
.aq-muted-note {
	display: flex;
	align-items: center;
	gap: 10px;
	margin: 0;
	color: var(--aq-muted);
	font-size: 14px;
}
.aq-fishes {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 14px;
	margin-bottom: 14px;
}
.aq-fish {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 14px 16px 16px;
	border: 1px solid var(--aq-border);
	border-radius: var(--aq-radius);
	background: var(--aq-surface);
	transition: border-color 0.2s var(--aq-ease), box-shadow 0.2s var(--aq-ease);
}
.aq-fish:hover {
	border-color: #b9d6f8;
	box-shadow: var(--aq-shadow-hover);
}
.aq-fish__head {
	display: flex;
	align-items: center;
	gap: 10px;
}
.aq-fish__num {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: var(--aq-ink);
	color: #fff;
	font-size: 13px;
	font-weight: 700;
}
.aq-fish__title {
	flex: 1;
	min-width: 0;
	color: var(--aq-ink);
	font-size: 15px;
	line-height: 1.35;
}
.aq-fish__actions {
	display: flex;
	gap: 4px;
}
.aq-fish__progress {
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.aq-fish__progress-label {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-fish__progress-label strong {
	color: var(--aq-ink);
	font-size: 14px;
	white-space: nowrap;
}
.aq-fish__warn {
	color: var(--aq-warning);
	font-size: 12px;
	font-weight: 600;
}
.aq-fish__more {
	display: inline-flex;
	align-self: flex-start;
	align-items: center;
	gap: 4px;
	min-height: 36px;
	margin: -6px 0 -6px -8px;
	padding: 4px 8px;
	border-radius: 8px;
	color: var(--aq-primary);
	font-size: 14px;
	font-weight: 600;
	transition: background 0.2s var(--aq-ease);
}
.aq-fish__more:hover {
	background: var(--aq-primary-soft);
}
.aq-fish__more:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-bar {
	height: 6px;
	border-radius: 999px;
	background: #edf2f8;
	overflow: hidden;
}
.aq-bar span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--aq-primary);
	transition: width 0.3s var(--aq-ease);
}
.aq-bar--over span {
	background: #e39a1b;
}
.aq-fish__stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	margin: 0;
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
}
.aq-fish__stats dt {
	color: var(--aq-muted);
	font-size: 12px;
	font-weight: 500;
}
.aq-fish__stats dd {
	margin: 2px 0 0;
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 600;
}
.aq-btn--sm {
	min-height: 36px;
	padding: 6px 12px;
	font-size: 14px;
}
.aq-btn--icon {
	width: 40px;
	min-height: 40px;
	padding: 0;
}
.aq-add {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: 52px;
	border: 1.5px dashed var(--aq-border-strong);
	border-radius: var(--aq-radius);
	color: var(--aq-primary);
	font-weight: 600;
	transition: background 0.2s var(--aq-ease), border-color 0.2s var(--aq-ease);
}
.aq-add:hover {
	border-color: var(--aq-primary);
	background: var(--aq-primary-soft);
}
.aq-add:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-panel {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px;
}
.aq-panel ::v-deep .aq-kpi {
	padding: 14px 16px;
	box-shadow: none;
}
.aq-panel ::v-deep .aq-kpi__value {
	font-size: 24px;
}
.aq-panel__status {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
}
.aq-panel__label {
	color: var(--aq-ink-2);
	font-size: 14px;
	font-weight: 600;
}
.aq-timeline {
	width: 100%;
	margin: 0;
	padding: 0;
	list-style: none;
}
.aq-timeline li {
	display: flex;
	flex-direction: column;
	padding: 6px 0 6px 14px;
	border-left: 2px solid var(--aq-border);
	font-size: 13px;
}
.aq-timeline span {
	color: var(--aq-muted);
}
.aq-timeline strong {
	color: var(--aq-ink);
	font-weight: 600;
}
.aq-panel__hint {
	margin: 0;
	color: var(--aq-muted);
	font-size: 13px;
	line-height: 1.45;
}
.aq-panel__actions {
	display: flex;
	flex-direction: column;
	gap: 10px;
}
.aq-panel__actions:not(:first-child) {
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
}
.aq-panel__actions:empty {
	display: none;
}
@media (max-width: 1280px) {
	.aq-layout {
		grid-template-columns: minmax(0, 1fr);
	}
	.aq-layout__side {
		position: static;
		order: -1;
	}
	.aq-panel {
		flex-flow: row wrap;
		align-items: flex-start;
	}
	.aq-panel > * {
		flex: 1 1 220px;
	}
	.aq-panel__actions {
		flex-direction: row;
		flex-wrap: wrap;
		flex-basis: 100%;
	}
	.aq-panel__actions .aq-btn--block {
		width: auto;
		flex: 1 1 180px;
	}
}
@media (max-width: 768px) {
	.aq-card__body {
		padding: 16px;
	}
	.aq-fish__stats {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
